import { useEffect, useMemo } from 'react';

import {
  useCurrentCompanyId,
  useCurrentCompanyOrFail,
  GetAscProjectsQuery,
  GetAscProjectsQueryVariables,
  GET_ASC_PROJECTS,
  Conditions,
  PredictedCapitalizationStatus,
  AscProjectSort,
  useCursorPaginatedQuery,
  keywordFilter,
  useSticky,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { AscProjectFilterableColumn } from '../types';
import { mapAscProjectFilterConditions, transformAscProject } from '../utils';

export type AscProject = Omit<
  GetAscProjectsQuery['getAscProjects']['edges'][number]['node'],
  | 'startDate'
  | 'endDate'
  | 'capitalizationStartDate'
  | 'capitalizationEndDate'
  | 'capitalizationEndDateExtendedFrom'
  | 'predictedCapitalizationStatus'
  | 'predictedCapitalizationStatusReason'
  | 'predictedCapitalizationStartDate'
  | 'predictedCapitalizationEndDate'
  | 'predictedCapitalizationEndDateReason'
> & {
  startDate: Date | null;
  endDate: Date | null;
  capitalizationStartDate: Date | null;
  capitalizationEndDate: Date | null;
  capitalizationEndDateExtendedFrom: Date | null;
  predictedCapitalizationStatus: PredictedCapitalizationStatus | null;
  predictedCapitalizationStatusReason: string | null;
  predictedCapitalizationStartDate: Date | null;
  predictedCapitalizationEndDate: Date | null;
  predictedCapitalizationEndDateReason: string | null;
  isCapitalized: boolean;
};

type UseAscProjectsProps = {
  filterConditions?: Conditions<AscProjectFilterableColumn>;
  searchKeyword?: string | null;
  sorting?: Array<AscProjectSort> | AscProjectSort;
  skip?: boolean;
  numElementsInitialFetch?: number;
  fetchAll?: boolean;
};

export const useAscProjects = ({
  fetchAll = false,
  filterConditions,
  numElementsInitialFetch,
  searchKeyword,
  skip = false,
  sorting,
}: UseAscProjectsProps = {}): {
  loading: boolean;
  ascProjects: AscProject[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const companyId = useCurrentCompanyId();
  const { lastCompletedAscSyncPeriodEnd } = useCurrentCompanyOrFail();
  const { fiscalYearId, periodName } = useAscContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetAscProjectsQuery, GetAscProjectsQueryVariables>(
    GET_ASC_PROJECTS,
    {
      variables: {
        companyId,
        fiscalYearId,
        periodName,
        filter: {
          and: [
            keywordFilter(['name', 'projectOwnerName'], searchKeyword),
            mapAscProjectFilterConditions(filterConditions, lastCompletedAscSyncPeriodEnd),
          ],
        },
        sorting: sorting ?? [],
      },
      skip,
    },
    {
      getConnection: (data) => data.getAscProjects,
      numElementsPerFetch: fetchAll ? 1000 : 40,
      numElementsInitialFetch,
    },
  );

  // Automatically fetch all pages when fetchAll is true
  useEffect(() => {
    if (fetchAll && !loading && !isLoadingMore && hasNextPage) {
      onLoadMoreItems();
    }
  }, [fetchAll, loading, isLoadingMore, hasNextPage, onLoadMoreItems]);

  // Keep existing data on the screen while a new query is running
  const stickyData = useSticky(requestData);
  const ascProjectNodes = stickyData?.getAscProjects?.edges.map((edge) => edge.node) ?? null;

  const ascProjects = useMemo(() => {
    if (!ascProjectNodes) {
      return null;
    }

    return ascProjectNodes.map((project) => transformAscProject(project));
  }, [ascProjectNodes]);

  if (loading && !ascProjects) {
    return {
      loading: true,
      ascProjects: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore: false,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    return {
      loading: true,
      ascProjects: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore: false,
    };
  }

  return {
    loading,
    ascProjects,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
